/**
 * Pure functions for formatting chart data.
 * Following the Data Transformation Patterns from REACT_PATTERNS.md
 * 
 * Benefits:
 * - Pure functions (no side effects)
 * - Easy to test
 * - Reusable
 * - Clear separation of data transformation from rendering
 */

import { Bucket } from "../types";

/**
 * Format a bucket_start (or YYYY-MM-DD) for X-axis / tooltip display.
 * Default bucket="day" preserves the pre-aggregate behaviour at unmigrated callers.
 */
export function formatDisplayDate(
  value: string,
  bucket: Bucket = "day",
): string {
  const date = new Date(value);
  const sameYearAsNow = date.getUTCFullYear() === new Date().getUTCFullYear();
  switch (bucket) {
    case "hour":
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC",
      });
    case "week":
      return `Week of ${date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      })}`;
    case "month":
      return date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
    case "day":
    default:
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: sameYearAsNow ? undefined : "numeric",
        timeZone: "UTC",
      });
  }
}

/**
 * Format number to fixed decimal places for display.
 */
export function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return "-";
  return score.toFixed(3);
}

/**
 * A failure share (0..1) as a whole percent. A non-zero rate under half a
 * percent reads "<1%" rather than rounding to "0%", which would contradict the
 * entry being on screen at all.
 */
export function formatFailRate(rate: number): string {
  const pct = rate * 100;
  if (pct > 0 && pct < 1) return "<1%";
  return `${Math.round(pct)}%`;
}
